import {
  QueryKey,
  QueryObserverResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useCallback, useSyncExternalStore } from "react";

import { useModule } from "@/hooks/use-module";

import {
  WaitlistCreate,
  WaitlistEntry,
  WaitlistStatus,
  WaitlistUpdate,
  WaitlistUpdateData,
} from "@/types/waitlist";

import { requestData } from "@/api/api-config";
import {
  previewWaitlistMessage,
  sendWaitlistMessage,
  testSendWaitlistMessage,
  uploadWaitlistMessageFiles,
} from "@/api/provider/waitlist-messages";
import {
  applyWaitlistUpdate,
  getWaitlistCacheScope,
  getWaitlistRecoveryStatus,
  readWaitlistCache,
  subscribeWaitlistRecovery,
  waitlistQueryKeys,
} from "@/query/waitlist-cache";
import { recoverWaitlistCache } from "@/query/waitlist-recovery";

export { waitlistQueryKeys } from "@/query/waitlist-cache";

export function usePreviewWaitlistMessage() {
  return useMutation({ mutationFn: previewWaitlistMessage });
}

export function useSendWaitlistMessage() {
  return useMutation({ mutationFn: sendWaitlistMessage });
}

export function useTestSendWaitlistMessage() {
  return useMutation({ mutationFn: testSendWaitlistMessage });
}

export function useUploadWaitlistMessageFiles() {
  return useMutation({
    mutationFn: ({ files }: { files: File[]; channel: "SMS" }) =>
      uploadWaitlistMessageFiles(files),
  });
}

// --- API functions ---

async function listWaitlistEntries(
  providerIds?: string[],
  desiredDate?: string,
  status?: WaitlistStatus[],
  patientId?: string,
  locationIds?: string[],
  matchProviderIds?: string[],
): Promise<WaitlistEntry[] | null> {
  return requestData<WaitlistEntry[]>(
    "waitlist",
    "get",
    {
      searchParams: {
        providerIds,
        desiredDate,
        status,
        patientId,
        locationIds,
        matchProviderIds,
      },
    },
    false,
  );
}

async function createWaitlistEntry(
  data: WaitlistCreate,
): Promise<WaitlistEntry | null> {
  return requestData<WaitlistEntry>("waitlist", "post", { json: data }, false);
}

async function updateWaitlistEntry(
  waitlistId: string,
  data: WaitlistUpdate,
): Promise<WaitlistEntry | null> {
  return requestData<WaitlistEntry>(
    `waitlist/${waitlistId}`,
    "patch",
    { json: data },
    false,
  );
}

async function deleteWaitlistEntry(
  waitlistId: string,
): Promise<WaitlistUpdateData | null> {
  return requestData<WaitlistUpdateData>(
    `waitlist/${waitlistId}`,
    "delete",
    {},
    false,
  );
}

// Capture the scope before starting HTTP, not when a delayed response completes.
function useWaitlistRead() {
  const client = useQueryClient();
  return <T>(key: readonly unknown[], fetch: () => Promise<T>) =>
    readWaitlistCache(client, getWaitlistCacheScope(client), key, fetch);
}
// Recovery failures must reach the existing unavailable/Retry props even while
// retaining last-known rows. Retry uses the same cache-scoped recovery GET.
function useWaitlistRecoveryResult<T>(
  query: UseQueryResult<T, Error>,
  key: QueryKey,
): UseQueryResult<T, Error> {
  const client = useQueryClient();
  const subscribe = useCallback(
    (listener: () => void) => subscribeWaitlistRecovery(client, listener),
    [client],
  );
  const readStatus = useCallback(
    () => getWaitlistRecoveryStatus(client),
    [client],
  );
  const recovery = useSyncExternalStore(subscribe, readStatus, readStatus);
  const refetch: typeof query.refetch = async (options) => {
    const scope = getWaitlistCacheScope(client);
    if (!scope.recovery && !scope.recoveryStatus.error)
      return query.refetch(options);
    let failure: Error | null = null;
    try {
      await recoverWaitlistCache(client, scope);
    } catch (error) {
      failure = error instanceof Error ? error : new Error(String(error));
      if (options?.throwOnError) throw error;
    }
    return {
      ...query,
      data: client.getQueryData<T>(key),
      error: failure,
      status: failure ? "error" : "success",
      isError: !!failure,
      isSuccess: !failure,
      isPending: false,
      isLoading: false,
      isFetching: false,
    } as QueryObserverResult<T, Error>;
  };
  const error = recovery.error ?? query.error;
  const isError = error !== null;
  return {
    ...query,
    error,
    isError,
    isSuccess: isError ? false : query.isSuccess,
    status: isError ? "error" : query.status,
    isPending: isError ? false : query.isPending,
    isLoading: isError ? false : query.isLoading,
    isFetching: query.isFetching || recovery.pending,
    isRefetching: query.isRefetching || recovery.pending,
    isRefetchError: isError && query.data !== undefined,
    isLoadingError: isError && query.data === undefined,
    refetch,
  } as UseQueryResult<T, Error>;
}

const synchronizedQueryOptions = {
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
} as const;

const activeWaitlistStatuses = [WaitlistStatus.WAITING];

// --- Query hooks ---

export function useWaitlistDetailQuery(waitlistId: string | null | undefined) {
  const hasWaitlist = useModule("WAITLIST");
  const read = useWaitlistRead();
  const key = waitlistQueryKeys.detail(waitlistId ?? "");
  const query = useQuery({
    queryKey: key,
    queryFn: () =>
      read(key, () =>
        requestData<WaitlistEntry>(`waitlist/${waitlistId}`, "get", {}, false),
      ),
    ...synchronizedQueryOptions,
    enabled: hasWaitlist && !!waitlistId,
  });
  return useWaitlistRecoveryResult(query, key);
}

export function useWaitlistForProviderDate(
  providerId: string | undefined,
  desiredDate: string | undefined,
  enabled = true,
) {
  const hasWaitlist = useModule("WAITLIST");
  const read = useWaitlistRead();
  const key = waitlistQueryKeys.list(
    providerId ? [providerId] : undefined,
    desiredDate,
    activeWaitlistStatuses,
  );
  const query = useQuery({
    queryKey: key,
    queryFn: () =>
      read(key, () =>
        listWaitlistEntries(
          providerId ? [providerId] : undefined,
          desiredDate,
          activeWaitlistStatuses,
        ),
      ),
    enabled: hasWaitlist && enabled && !!providerId && !!desiredDate,
    ...synchronizedQueryOptions,
  });
  return useWaitlistRecoveryResult(query, key);
}

export function useWaitlistForProvidersDate(
  providerIds: string[] | undefined,
  desiredDate: string | undefined,
  enabled = true,
  locationIds?: string[],
  statuses: WaitlistStatus[] = activeWaitlistStatuses,
  // Filters nothing: each entry's `matchingProviderIds` says which of these
  // providers could take it, all in this one request.
  matchProviderIds?: string[],
) {
  const hasWaitlist = useModule("WAITLIST");
  const read = useWaitlistRead();
  const sortedIds = providerIds?.length
    ? [...new Set(providerIds)].sort()
    : undefined;
  const sortedLocationIds = locationIds?.length
    ? [...new Set(locationIds)].sort()
    : undefined;
  const sortedMatchIds = matchProviderIds?.length
    ? Array.from(new Set(matchProviderIds)).sort()
    : undefined;
  const key = waitlistQueryKeys.list(
    sortedIds,
    desiredDate,
    statuses,
    undefined,
    sortedLocationIds,
    sortedMatchIds,
  );
  const query = useQuery({
    queryKey: key,
    queryFn: () =>
      read(key, () =>
        listWaitlistEntries(
          sortedIds,
          desiredDate,
          statuses,
          undefined,
          sortedLocationIds,
          sortedMatchIds,
        ),
      ),
    enabled:
      hasWaitlist &&
      enabled &&
      (providerIds === undefined || !!sortedIds?.length) &&
      !!desiredDate,
    // When only the day's providers change (a booking for someone new), keep
    // showing the list while it reloads. Another day or location starts empty.
    placeholderData: (previous, previousQuery) => {
      const before = previousQuery?.queryKey[2];
      return sortedMatchIds &&
        before?.matchProviderIds &&
        before.desiredDate === desiredDate &&
        before.locationIds?.join() === sortedLocationIds?.join()
        ? previous
        : undefined;
    },
    ...synchronizedQueryOptions,
  });
  return useWaitlistRecoveryResult(query, key);
}

export function useWaitlistForDate(
  desiredDate: string | undefined,
  enabled = true,
) {
  const hasWaitlist = useModule("WAITLIST");
  const read = useWaitlistRead();
  const key = waitlistQueryKeys.list(
    undefined,
    desiredDate,
    activeWaitlistStatuses,
  );
  const query = useQuery({
    queryKey: key,
    queryFn: () =>
      read(key, () =>
        listWaitlistEntries(undefined, desiredDate, activeWaitlistStatuses),
      ),
    enabled: hasWaitlist && enabled && !!desiredDate,
    ...synchronizedQueryOptions,
  });
  return useWaitlistRecoveryResult(query, key);
}

// --- Mutation hooks ---

function useWaitlistMutationReconciler() {
  const client = useQueryClient();
  return async <T extends WaitlistEntry | WaitlistUpdateData | null>(
    mutate: () => Promise<T>,
  ): Promise<T> => {
    const scope = getWaitlistCacheScope(client);
    const changes = new Map<string, WaitlistUpdateData>();
    scope.pendingReads.add(changes);
    try {
      const result = await mutate();
      if (result) {
        const change: WaitlistUpdateData =
          "waitlistId" in result
            ? result
            : {
                waitlistId: result.id,
                entry: result,
                locationId: result.locationId,
              };
        // Updates received while this request was pending are already cached.
        // An older serialized HTTP result must not replace them.
        if (!changes.has(change.waitlistId))
          applyWaitlistUpdate(client, scope, change);
      }
      return result;
    } finally {
      scope.pendingReads.delete(changes);
    }
  };
}

export function useCreateWaitlistMutation() {
  const reconcile = useWaitlistMutationReconciler();
  return useMutation({
    mutationFn: (data: WaitlistCreate) =>
      reconcile(() => createWaitlistEntry(data)),
  });
}

export function useUpdateWaitlistMutation() {
  const reconcile = useWaitlistMutationReconciler();
  return useMutation({
    mutationFn: ({
      waitlistId,
      data,
    }: {
      waitlistId: string;
      data: WaitlistUpdate;
    }) => reconcile(() => updateWaitlistEntry(waitlistId, data)),
  });
}

export function useDeleteWaitlistMutation() {
  const reconcile = useWaitlistMutationReconciler();
  return useMutation({
    mutationFn: (waitlistId: string) =>
      reconcile(() => deleteWaitlistEntry(waitlistId)),
  });
}
